//MUI Imports
import { Box, Grid, Stack } from "@mui/material";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import AddCircleRounded from "@mui/icons-material/AddCircleRounded";

import InfoSubTitle from "../../../common/InfoSubTitle";
// Layouts
import SmartSelector from "@/Layouts/components/InputComponents/SmartSelector";
import SimpleTextField from "@/Layouts/components/InputComponents/SimpleTextField";
import SimpleDoubleChecking from "@/Layouts/components/Buttons/SimpleDoubleChecking";
import DoubleCheckingButton from "@/Layouts/components/Buttons/DoubleCheckingButton";
import SimpleIconButton from "@/Layouts/components/Buttons/SimpleIconButton";

import { useMemo } from "react";

import { useQueries, useQuery } from "@tanstack/react-query";
import { getAppendingConfigInfo, getSystemSettingsAdditionalInfo } from "@/Settings/api/SystemSettings/queries";
import { resolveOems } from "@/Settings/helpers/SystemSettings/appendingConfiguration";
import { useLocations } from "@/Common/hooks/Location/useLocations";
import { tenant } from "@/Constants/globalConfig";
import { getAdministrationTools } from "@/Settings/api/AdministrationTools/queries";
import {
	ICrossStoreEmailErrors,
	IInputCrossStoreEmailDistribution,
	TEmailDistributionUpdateParams,
} from "@/Settings/types/SystemSettings/emailDistribution";
import isEqual from "lodash-es/isEqual";

import getSelectEmails from "@/Settings/helpers/SystemSettings/emailDistribution/getSelectEmails";
import { ICrossStoreAccountDetails } from "@/Settings/types/Common/model";
import TrashIconWhite from "@/assets/icons/motivators/spiffSetup/trash-white.svg";

type TEmailDistributionLayoutProps = {
	locationId: string;
	distribution: IInputCrossStoreEmailDistribution;
	errors: ICrossStoreEmailErrors;
	isPending: boolean;
	onActionUpdate: ({ index, key, value }: TEmailDistributionUpdateParams) => void;
	onRemove: (index: number) => void;
	index: number;
};
const getSelectedEmployees = (
	employees: { employeeId?: string; _id?: string; fullName?: string }[] = [],
	selected: {
		employeeId?: string;
		fullName?: string;
		_id?: string;
	}[] = []
) => {
	const selectedKeys = new Set(
		selected
			.flatMap((item) => [item.employeeId, item._id, item.fullName])
			.filter((value): value is string => typeof value === "string" && value.trim().length > 0)
	);
	return employees?.filter(
		(employee) =>
			(Boolean(employee.employeeId) && selectedKeys.has(String(employee.employeeId))) ||
			(Boolean(employee._id) && selectedKeys.has(String(employee._id))) ||
			(Boolean(employee.fullName) && selectedKeys.has(String(employee.fullName)))
	);
};

const dropdownStyles = { width: "216px", maxWidth: "216px" };
const crossStoreFieldWidth = 172;
const crossStoreFieldGap = 3; // 16px (theme spacing)
const destinationGlToSourceLabelGap = 6.25; // 50px (theme spacing)
const destinationLabelControlTypeGap = 7.5; // 60px (theme spacing)
const crossStoreInputHeight = 30;
const crossStoreTitleSx = { "& .MuiTypography-root": { fontSize: "15px !important" } };

const EmailDistributionLayout = (props: TEmailDistributionLayoutProps) => {
	const { locationId, distribution, index, errors, isPending, onActionUpdate, onRemove } = props;
	const oems = resolveOems();
	const { locations = [], isLoading: isLoadingLocations } = useLocations(tenant);
	const hasToNonEmployeeError = Boolean(props.errors?.toNonEmployee);

	const { data: additionalInfo } = useQuery({
		queryKey: ["systemSettingsAdditionalInfo", locationId],
		queryFn: () => getSystemSettingsAdditionalInfo({ locationId }),
		enabled: Boolean(locationId),
		staleTime: Infinity,
	});

	const { data: appendingConfigInfoData } = useQuery({
		queryKey: ["appendingConfigInfo", locationId],
		queryFn: () => getAppendingConfigInfo({ locationId, oems }),
		enabled: Boolean(locationId) && Boolean(oems?.length),
		staleTime: Infinity,
	});

	const employeeListData = additionalInfo?.getEmployees as { employeeId: string; fullName: string }[] | undefined;
	const glAccountsData = appendingConfigInfoData?.getGlAccounts;

	const glAccountOptions = (glAccountsData ?? [])
		.map((item: { glAccount?: { glAccount?: string; accountType?: string }; accountDescription?: string }) => {
			const value = item?.glAccount?.glAccount ?? "";
			return {
				...item,
				value,
				label: value,
			};
		})
		.filter((item: { value: string }) => Boolean(item.value));

	const uniqueGlAccountOptions = (() => {
		const seen = new Set<string>();
		return glAccountOptions.filter((item: { value: string }) => {
			if (seen.has(item.value)) {
				return false;
			}
			seen.add(item.value);
			return true;
		});
	})();

	const destinationStoreOptions = (locations ?? [])
		.filter((loc) => loc?.locationId && loc.locationId !== locationId)
		.map((loc) => ({ label: loc.name, value: loc.locationId }))
		.sort((a, b) => String(a.label).localeCompare(String(b.label)));

	const destinationStoreIds = useMemo(() => {
		const ids = (distribution?.accountMappings ?? [])
			.map((account) => {
				return account.destinationStore.storeId;
			})
			.filter(Boolean);
		return Array.from(new Set(ids));
	}, [distribution?.accountMappings, destinationStoreOptions]);

	const destinationAdminToolsQueries = useQueries({
		queries: destinationStoreIds.map((storeId) => ({
			queryKey: ["administrationTools", storeId],
			queryFn: () => getAdministrationTools(storeId),
			enabled: Boolean(storeId),
			staleTime: Infinity,
		})),
	});

	const destinationAdminToolsByStoreId = useMemo(() => {
		const map = new Map<string, { data: any; isFetching: boolean; status: string }>();
		destinationStoreIds.forEach((storeId, idx) => {
			const query = destinationAdminToolsQueries[idx];
			map.set(storeId, {
				data: query?.data,
				isFetching: Boolean(query?.isFetching),
				status: String(query?.status ?? ""),
			});
		});
		return map;
	}, [destinationAdminToolsQueries, destinationStoreIds]);

	/********************************************* HANDLERS ************************************************/
	const handleToIndividuals = (selected: { employeeId: string; _id: string }[]) => {
		const updated = [...(distribution?.toIndividuals ?? [])];
		const selectedEmployees = selected.map((employee) => ({ employeeId: employee.employeeId, _id: employee._id }));
		if (!isEqual(selectedEmployees, updated)) {
			onActionUpdate({ index, key: "toIndividuals", value: selectedEmployees });
		}
	};

	const handleEmailValues = (emailValues: string) => {
		const splitEmails: string[] = emailValues?.split(",").map((element) => element.trim());
		onActionUpdate({ index, key: "toNonEmployee", value: splitEmails });
	};

	const handleUpdateSourceGlAccount = (selected: { value?: string; glAccount?: { glAccount?: string } } | null, key: number) => {
		const updated = [...(distribution?.accountMappings ?? [])];
		updated[key].sourceGlAccount = selected?.value || selected?.glAccount?.glAccount || "";
		onActionUpdate({ index, key: "crossStoreAccountMappings", value: updated });
	};

	const handleDestinationStore = (selected: { label: string; value: string } | null, key: number) => {
		const updated = [...(distribution?.accountMappings ?? [])];
		const nextStoreId = selected?.value;
		const prevStore = updated[key].destinationStore.storeId;

		updated[key].destinationStore = {
			storeId: selected?.value || "",
			storeName: selected?.label || "",
		};

		if (!nextStoreId) {
			updated[key].destinationSourceLabel = { label: "", value: "" };
			updated[key].destinationControlType = { label: "", value: "", identifier: "" };
			onActionUpdate({ index, key: "crossStoreAccountMappings", value: updated });
			return;
		}

		// If store changes, reset dependent fields immediately.
		if (prevStore !== nextStoreId) {
			updated[key].destinationSourceLabel = { label: "", value: "" };
			updated[key].destinationControlType = { label: "", value: "", identifier: "" };
		}
		onActionUpdate({ index, key: "crossStoreAccountMappings", value: updated });
	};

	const handleDestinationGlAccount = (text: string, key: number) => {
		const updated = [...(distribution?.accountMappings ?? [])];
		updated[key].destinationGlAccount = text;
		onActionUpdate({ index, key: "crossStoreAccountMappings", value: updated });
	};

	const handleDestinationSourceLabel = (selected: { label: string; _id: string; value: string }, key: number) => {
		const updated = [...(distribution?.accountMappings ?? [])];
		updated[key].destinationSourceLabel = { label: selected?.label ?? "", value: selected?.value ?? "" };
		onActionUpdate({ index, key: "crossStoreAccountMappings", value: updated });
	};

	const handleDestinationControlType = (selected: { identifier?: string; label?: string; value?: string }, key: number) => {
		const updated = [...(distribution?.accountMappings ?? [])];
		updated[key].destinationControlType = {
			label: selected?.label ?? "",
			value: selected?.value ?? "",
			identifier: selected.identifier ?? "",
		};
		onActionUpdate({ index, key: "crossStoreAccountMappings", value: updated });
	};

	const handleRemoveAccount = (key: number) => {
		const updated = [...(distribution?.accountMappings ?? [])];
		updated.splice(key, 1);
		onActionUpdate({ index, key: "crossStoreAccountMappings", value: updated });
	};
	const handleAddAccount = () => {
		const updated = [...(distribution?.accountMappings ?? [])];
		updated.push({
			sourceGlAccount: "",
			destinationStore: {
				storeId: "",
				storeName: "",
			},
			destinationGlAccount: "",
			destinationSourceLabel: { label: "", value: "" },
			destinationControlType: { label: "", value: "", identifier: "" },
		});
		onActionUpdate({ index, key: "crossStoreAccountMappings", value: updated });
	};

	return (
		<Grid container item xs>
			<Grid item container sx={{ columnGap: crossStoreFieldGap, rowGap: 1.5 }}>
				<Grid
					item
					xs="auto"
					sx={{
						width: "216px",
						maxWidth: "216px",
						flex: "0 0 216px",
						minWidth: "216px",
					}}
				>
					<Box sx={crossStoreTitleSx}>
						<InfoSubTitle title="To Individual(s):" className="!mb-[12px]" />
					</Box>
					<SmartSelector
						multiple
						tagsVariant={"text"}
						selectBy={"employeeId"}
						showBy={"fullName"}
						options={employeeListData || []}
						selected={getSelectedEmployees(employeeListData, distribution?.toIndividuals)}
						inputProps={{
							placeholder: "Select an Option",
						}}
						listBoxProps={{
							sx: { maxHeight: "220px" },
						}}
						clearable
						disabled={isPending}
						error={Boolean(errors?.toIndividuals)}
						sx={dropdownStyles}
						onChangeAction={(selected) => handleToIndividuals(selected as { employeeId: string; _id: string }[])}
					/>
				</Grid>
				<Grid
					item
					xs="auto"
					sx={{
						width: "216px",
						maxWidth: "216px",
						flex: "0 0 216px",
					}}
				>
					<Box sx={crossStoreTitleSx}>
						<InfoSubTitle title="To Non-Employees:" className="!mb-[12px]" />
					</Box>
					<SimpleTextField
						sx={{ width: "216px", maxWidth: "216px" }}
						classes={hasToNonEmployeeError ? "!shadow-thin-red-line !bg-light-error" : ""}
						editable
						outlined
						disabled={isPending}
						skeletonWidth={216}
						text={getSelectEmails(distribution?.toNonEmployee)}
						placeholder={"john.doe@gmail.com, d.smith@hotmail.com"}
						error={hasToNonEmployeeError}
						onActionUpdate={handleEmailValues}
					/>
				</Grid>
				<Grid item xs={3} mt={-1}>
					<DoubleCheckingButton
						title={"Remove Email distribution"}
						sx={{ marginLeft: "-5px" }}
						endIcon={<img src={TrashIconWhite} className="w-4.5" />}
						confirmationButtonClasses="!w-[190px] hover:!border-gray-blue hover:!bg-gray-blue hover:!shadow-gray-blue"
						mainButtonClasses={"btn-simple-remove btn-pack-remove"}
						mainSx={{ marginLeft: "10px" }}
						confirmationSx={{ right: 0, left: "10px" }}
						onDeleteAction={() => onRemove(index)}
					/>
				</Grid>
				<Grid item xs={12} className="!mt-[24px]">
					<Stack direction="row" columnGap={crossStoreFieldGap} alignItems="center">
						<Box
							sx={{
								width: `${crossStoreFieldWidth}px`,
								flex: `0 0 ${crossStoreFieldWidth}px`,
								height: `${crossStoreInputHeight}px`,
								display: "flex",
								alignItems: "center",
								whiteSpace: "nowrap",
								...crossStoreTitleSx,
							}}
						>
							<InfoSubTitle title="Source GL Account:" />
						</Box>
						<Box
							sx={{
								width: `${crossStoreFieldWidth}px`,
								flex: `0 0 ${crossStoreFieldWidth}px`,
								height: `${crossStoreInputHeight}px`,
								display: "flex",
								alignItems: "center",
								whiteSpace: "nowrap",
								...crossStoreTitleSx,
							}}
						>
							<InfoSubTitle title="Destination Store:" />
						</Box>
						<Stack direction="row" columnGap={destinationGlToSourceLabelGap} alignItems="center">
							<Box
								sx={{
									width: `${crossStoreFieldWidth}px`,
									flex: `0 0 ${crossStoreFieldWidth}px`,
									height: `${crossStoreInputHeight}px`,
									display: "flex",
									alignItems: "center",
									whiteSpace: "nowrap",
									...crossStoreTitleSx,
								}}
							>
								<InfoSubTitle title="Destination GL Account:" />
							</Box>
							<Stack direction="row" columnGap={destinationLabelControlTypeGap} alignItems="center">
								<Box
									sx={{
										width: `${crossStoreFieldWidth}px`,
										flex: `0 0 ${crossStoreFieldWidth}px`,
										height: `${crossStoreInputHeight}px`,
										display: "flex",
										alignItems: "center",
										whiteSpace: "nowrap",
										...crossStoreTitleSx,
									}}
								>
									<InfoSubTitle title="Destination Source Label:" />
								</Box>
								<Box
									sx={{
										width: `${crossStoreFieldWidth}px`,
										flex: `0 0 ${crossStoreFieldWidth}px`,
										height: `${crossStoreInputHeight}px`,
										display: "flex",
										alignItems: "center",
										whiteSpace: "nowrap",
										...crossStoreTitleSx,
									}}
								>
									<InfoSubTitle title="Destination Control Type:" />
								</Box>
							</Stack>
						</Stack>
					</Stack>
				</Grid>
				{distribution?.accountMappings?.map((account: ICrossStoreAccountDetails, key: number) => {
					const keyIndex = `cross-Store-Email-Distribution-${index}-account-details-${key}`;

					const destinationStoreId = account?.destinationStore.storeId || "";
					const destinationToolsQuery = destinationStoreId ? destinationAdminToolsByStoreId.get(destinationStoreId) : undefined;
					const destinationTools = destinationToolsQuery?.data;
					const destinationSourceLabelOptions: { label: string; _id: string; value: string }[] =
						destinationTools?.integrations?.dmsSources ?? [];
					const destinationControlTypeOptions: { identifier?: string; label?: string; value?: string }[] =
						destinationTools?.integrations?.dmsControlTypes ?? [];
					const hasDestinationGlAccountError = Boolean(errors?.accountMappings?.[key]?.destinationGlAccount);
					const isLoadingDestinationTools =
						Boolean(destinationStoreId) && (destinationToolsQuery?.isFetching || destinationToolsQuery?.status === "pending");
					return (
						<Stack key={keyIndex} direction="row" alignItems="center" gap={2.5}>
							<Stack direction="row" gap={crossStoreFieldGap} alignItems="center">
								<Box
									sx={{
										width: `${crossStoreFieldWidth}px`,
										flex: `0 0 ${crossStoreFieldWidth}px`,
										height: `${crossStoreInputHeight}px`,
										display: "flex",
										alignItems: "center",
									}}
								>
									<SmartSelector
										showBy={"label"}
										selectBy={"value"}
										options={uniqueGlAccountOptions}
										selected={
											uniqueGlAccountOptions.find(
												(item: { value: string }) => item.value === account?.sourceGlAccount
											) ?? null
										}
										inputProps={{
											placeholder: "GL Account",
										}}
										listBoxProps={{
											sx: { maxHeight: "220px" },
										}}
										error={Boolean(errors?.accountMappings?.[key]?.sourceGlAccount)}
										disabled={isPending}
										onChangeAction={(selected) =>
											handleUpdateSourceGlAccount(
												selected as { value?: string; glAccount?: { glAccount?: string } } | null,
												key
											)
										}
									/>
								</Box>
								<Box
									sx={{
										width: `${crossStoreFieldWidth}px`,
										flex: `0 0 ${crossStoreFieldWidth}px`,
										height: `${crossStoreInputHeight}px`,
										display: "flex",
										alignItems: "center",
									}}
								>
									<SmartSelector
										showBy={"label"}
										selectBy={"value"}
										options={destinationStoreOptions}
										popperProps={{
											sx: {
												width: "232px !important",
											},
										}}
										selected={
											destinationStoreOptions.find(
												(item) =>
													item.value === account?.destinationStore.storeId ||
													item.label === account?.destinationStore.storeName
											) ?? null
										}
										inputProps={{
											placeholder: "Store",
										}}
										listBoxProps={{
											sx: { maxHeight: "220px" },
										}}
										error={Boolean(errors?.accountMappings?.[key]?.destinationStore)}
										disabled={isPending || isLoadingLocations}
										loading={isLoadingLocations}
										onChangeAction={(selected) =>
											handleDestinationStore(selected as { label: string; value: string } | null, key)
										}
									/>
								</Box>
								<Stack direction="row" columnGap={destinationGlToSourceLabelGap} alignItems="center">
									<Box
										sx={{
											width: `${crossStoreFieldWidth}px`,
											flex: `0 0 ${crossStoreFieldWidth}px`,
											height: `${crossStoreInputHeight}px`,
											display: "flex",
											alignItems: "center",
										}}
									>
										<SimpleTextField
											sx={{
												width: `${crossStoreFieldWidth}px`,
												"& .MuiInputBase-root": {
													minHeight: "30px",
												},
												"& .MuiInputBase-input": {
													paddingTop: "4.5px",
													paddingBottom: "4.5px",
													paddingLeft: "10px",
													paddingRight: "10px",
													fontSize: "14.5px",
												},
											}}
											editable
											outlined
											text={account?.destinationGlAccount || ""}
											placeholder={"GL Account"}
											disabled={isPending}
											error={hasDestinationGlAccountError}
											classes={hasDestinationGlAccountError ? "!shadow-thin-red-line !bg-light-error" : ""}
											onActionUpdate={(text: string) => handleDestinationGlAccount(text, key)}
										/>
									</Box>
									<Stack direction="row" columnGap={destinationLabelControlTypeGap} alignItems="center">
										<Box
											sx={{
												width: `${crossStoreFieldWidth}px`,
												flex: `0 0 ${crossStoreFieldWidth}px`,
												height: `${crossStoreInputHeight}px`,
												display: "flex",
												alignItems: "center",
											}}
										>
											<SmartSelector
												showBy={"label"}
												selectBy={"value"}
												options={destinationSourceLabelOptions}
												selected={
													destinationSourceLabelOptions.find(
														(item: { value: string }) => item.value === account?.destinationSourceLabel?.value
													) ?? null
												}
												inputProps={{
													placeholder: "Source Label",
												}}
												listBoxProps={{
													sx: { maxHeight: "220px" },
												}}
												error={Boolean(errors?.accountMappings?.[key]?.destinationStoreLabel)}
												disabled={isPending || !destinationStoreId || isLoadingDestinationTools}
												loading={isLoadingDestinationTools}
												onChangeAction={(selected) =>
													handleDestinationSourceLabel(
														selected as { label: string; _id: string; value: string },
														key
													)
												}
											/>
										</Box>
										<Box
											sx={{
												width: `${crossStoreFieldWidth}px`,
												flex: `0 0 ${crossStoreFieldWidth}px`,
												height: `${crossStoreInputHeight}px`,
												display: "flex",
												alignItems: "center",
											}}
										>
											<SmartSelector
												showBy={"label"}
												selectBy={"value"}
												options={destinationControlTypeOptions}
												selected={
													destinationControlTypeOptions.find(
														(item: { identifier?: string; label?: string; value?: string }) =>
															item.value === account?.destinationControlType?.value
													) ?? null
												}
												inputProps={{
													placeholder: "Control Type",
												}}
												listBoxProps={{
													sx: { maxHeight: "220px" },
												}}
												error={Boolean(errors?.accountMappings?.[key]?.destinationControlType)}
												disabled={isPending || !destinationStoreId || isLoadingDestinationTools}
												loading={isLoadingDestinationTools}
												onChangeAction={(selected) =>
													handleDestinationControlType(
														selected as { identifier?: string; label?: string; value?: string },
														key
													)
												}
											/>
										</Box>
									</Stack>
								</Stack>
							</Stack>
							<Box sx={{ height: `${crossStoreInputHeight}px`, display: "flex", alignItems: "center" }}>
								<SimpleDoubleChecking
									className={"btn-delete"}
									disabled={isPending}
									color={"var(--gray-blue-color)"}
									confirmationButtonColor={"var(--gray-blue-color)"}
									icon={<CloseRoundedIcon fontSize="small" />}
									iconHover={<CloseRoundedIcon fontSize="small" />}
									confirmationIcon={<DeleteOutlineOutlinedIcon fontSize="small" />}
									onDeleteAction={() => handleRemoveAccount(key)}
								/>
							</Box>
						</Stack>
					);
				})}
				<Grid item xs={10} className="flex justify-center items-center !mt-[24px]">
					<SimpleIconButton
						className={"btn-add"}
						color={"var(--primary-color)"}
						disabled={false}
						icon={<AddCircleOutlineRoundedIcon fontSize="large" />}
						iconHover={<AddCircleRounded fontSize="large" />}
						onClick={handleAddAccount}
					/>
				</Grid>
			</Grid>
		</Grid>
	);
};

export default EmailDistributionLayout;
